import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthData, clearAuth, setAuthStatus } from '../store/auth/slice';
import { authSelector } from '../store/auth/selector';
import useLocalStorage from './useLocalStorage';
import { secureStorage } from '../utils/secureStorage';

export const useAuthRedux = () => {
  const dispatch = useDispatch();
  const auth = useSelector(authSelector);
  const [storedAuth, setStoredAuth] = useLocalStorage('auth-ts', {
    isAuthenticated: false,
    token: null,
    currentUser: null,
  });

  // Sync localStorage with Redux on mount
  useEffect(() => {
    if (storedAuth.token && !auth.isAuthenticated) {
      dispatch(setAuthData({
        token: storedAuth.token,
        user: storedAuth.currentUser,
      }));
    }
  }, [storedAuth, auth.isAuthenticated, dispatch]);

  const login = useCallback((token: string, user: any) => {
    dispatch(setAuthData({ token, user }));
    
    // Store token securely
    secureStorage.setItem('token', token);
    secureStorage.setItem('currentUser', JSON.stringify(user));
    
    setStoredAuth({
      isAuthenticated: true,
      token,
      currentUser: user,
    });
  }, [dispatch, setStoredAuth]);

  const logout = useCallback(() => {
    dispatch(clearAuth());
    
    // Clear secure storage
    secureStorage.removeItem('token');
    secureStorage.removeItem('currentUser');
    
    setStoredAuth({
      isAuthenticated: false,
      token: null,
      currentUser: null,
    });
  }, [dispatch, setStoredAuth]);

  const updateAuthStatus = useCallback((status: boolean) => {
    dispatch(setAuthStatus(status));
    setStoredAuth((prev: any) => ({
      ...prev,
      isAuthenticated: status,
    }));
  }, [dispatch, setStoredAuth]);

  return {
    ...auth,
    login,
    logout,
    setAuthStatus: updateAuthStatus,
  };
};
